import React, { useState, useMemo } from 'react';
import { Modal, ModalOverlay, ModalContent, ModalBody } from '@chakra-ui/react';
import { MdClose } from 'react-icons/md';
import { RefreshCw } from 'lucide-react';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import { ActionPlan, RecurrenceType } from '../types';
import { countRecurringTasks } from '../helpers';

// ── Recurrence config ─────────────────────────────────────────────────────────

const RECURRENCE_OPTIONS: { value: RecurrenceType; label: string; sublabel: string }[] = [
    { value: 'none',    label: 'Aucune',       sublabel: 'Plan simple sans tâches automatiques' },
    { value: 'daily',   label: 'Quotidienne',  sublabel: 'Une tâche générée chaque jour (max 90)' },
    { value: 'weekly',  label: 'Hebdomadaire', sublabel: 'Une tâche générée chaque semaine (max 52)' },
    { value: 'monthly', label: 'Mensuelle',    sublabel: 'Une tâche générée chaque mois (max 12)' },
];

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
    isOpen: boolean;
    onClose: () => void;
    objectiveId: string;
    teamMembers: { id: string; name: string }[];
    onSubmit: (plan: ActionPlan) => void;
}

const CreateActionPlanModal: React.FC<Props> = ({ isOpen, onClose, objectiveId, teamMembers, onSubmit }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [responsibleUserId, setResponsibleUserId] = useState('');
    const [budget, setBudget] = useState('');
    const [deadline, setDeadline] = useState('');
    const [estimatedKPIImpact, setEstimatedKPIImpact] = useState('30');
    const [recurrence, setRecurrence] = useState<RecurrenceType>('none');
    const [recurrenceTaskTitle, setRecurrenceTaskTitle] = useState('');

    const taskCount = useMemo(
        () => (recurrence !== 'none' && deadline ? countRecurringTasks(recurrence, deadline) : 0),
        [recurrence, deadline],
    );

    const reset = () => {
        setName(''); setDescription(''); setResponsibleUserId('');
        setBudget(''); setDeadline(''); setEstimatedKPIImpact('30');
        setRecurrence('none'); setRecurrenceTaskTitle('');
    };

    const handleClose = () => { reset(); onClose(); };

    const handleSubmit = () => {
        if (!name.trim() || !deadline) return;
        const plan: ActionPlan = {
            id: `ap-${Date.now()}`,
            objectiveId,
            name: name.trim(),
            description: description.trim(),
            responsibleUserId,
            budget: parseFloat(budget) || 0,
            deadline,
            estimatedKPIImpact: parseInt(estimatedKPIImpact) || 0,
            status: 'not-started',
            recurrence,
            recurrenceTaskTitle: recurrenceTaskTitle.trim(),
        };
        onSubmit(plan);
        reset();
        onClose();
    };

    const sInput = 'w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-toola-500';

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="lg">
            <ModalOverlay className="bg-[#000] !opacity-50 backdrop-blur-sm" />
            <ModalContent className="md:top-[5vh] !overflow-hidden !z-[1002]">
                <ModalBody className="p-0">
                    <Card extra="p-6 max-h-[88vh] overflow-y-auto">

                        {/* Header */}
                        <div className="flex items-start justify-between mb-5 pb-4 border-b border-gray-100">
                            <div>
                                <h2 className="text-xl font-semibold text-navy-700">Nouveau plan d'action</h2>
                                <p className="text-sm text-gray-500 mt-0.5">Définissez les actions et leur récurrence</p>
                            </div>
                            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 mt-0.5">
                                <MdClose className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-5">
                            <InputField
                                label="Nom du plan *"
                                placeholder="Ex : Révision des procédures qualité"
                                id="plan-name"
                                type="text"
                                value={name}
                                onChange={(e: any) => setName(e.target.value)}
                            />

                            <div>
                                <label className="text-sm font-medium text-navy-700 mb-2 block">Description</label>
                                <textarea
                                    rows={2}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Décrivez les actions prévues…"
                                    className={`${sInput} resize-none`}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-navy-700 mb-2 block">Responsable</label>
                                    <select value={responsibleUserId} onChange={(e) => setResponsibleUserId(e.target.value)} className={sInput}>
                                        <option value="">Non assigné</option>
                                        {teamMembers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                                    </select>
                                </div>
                                <InputField
                                    label="Date d'échéance *"
                                    id="plan-deadline"
                                    type="date"
                                    value={deadline}
                                    onChange={(e: any) => setDeadline(e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <InputField
                                    label="Budget (€)"
                                    id="plan-budget"
                                    type="number"
                                    placeholder="0"
                                    value={budget}
                                    onChange={(e: any) => setBudget(e.target.value)}
                                />
                                <div>
                                    <label className="text-sm font-medium text-navy-700 mb-2 block">
                                        Impact estimé sur KPIs : <span className="text-toola-500 font-bold">{estimatedKPIImpact}%</span>
                                    </label>
                                    <input
                                        type="range" min={0} max={100} value={estimatedKPIImpact}
                                        onChange={(e) => setEstimatedKPIImpact(e.target.value)}
                                        className="w-full h-2 rounded-full appearance-none cursor-pointer mt-3"
                                        style={{ accentColor: '#0260CB' }}
                                    />
                                </div>
                            </div>

                            {/* Recurrence */}
                            <div>
                                <label className="text-sm font-medium text-navy-700 mb-3 block flex items-center gap-2">
                                    <RefreshCw size={14} className="text-toola-400" /> Récurrence des tâches
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {RECURRENCE_OPTIONS.map(({ value, label, sublabel }) => (
                                        <button
                                            key={value}
                                            onClick={() => setRecurrence(value)}
                                            className={`rounded-xl border-2 p-3 text-left transition-all ${
                                                recurrence === value
                                                    ? 'border-toola-400 bg-blue-50'
                                                    : 'border-gray-100 hover:border-gray-200 bg-white'
                                            }`}
                                        >
                                            <p className={`text-sm font-semibold ${recurrence === value ? 'text-toola-600' : 'text-navy-700'}`}>
                                                {recurrence === value && value !== 'none' && <RefreshCw size={11} className="inline mr-1 text-toola-400" />}
                                                {label}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">{sublabel}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Recurring task title */}
                            {recurrence !== 'none' && (
                                <div className="rounded-xl border border-toola-100 bg-blue-50/50 p-4 space-y-3">
                                    <InputField
                                        label="Titre des tâches générées *"
                                        placeholder="Ex : Contrôle qualité hebdomadaire"
                                        id="plan-task-title"
                                        type="text"
                                        value={recurrenceTaskTitle}
                                        onChange={(e: any) => setRecurrenceTaskTitle(e.target.value)}
                                    />
                                    {deadline && recurrenceTaskTitle && taskCount > 0 && (
                                        <div className="rounded-lg bg-toola-500 text-white px-3 py-2.5 text-sm">
                                            <p className="font-semibold">
                                                ✓ {taskCount} tâche{taskCount > 1 ? 's' : ''} seront créées automatiquement
                                            </p>
                                            <p className="text-xs text-blue-200 mt-0.5">
                                                Exemple : « {recurrenceTaskTitle} — 01 jan » → « {recurrenceTaskTitle} — {new Date(deadline).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} »
                                            </p>
                                        </div>
                                    )}
                                    {!recurrenceTaskTitle && (
                                        <p className="text-xs text-orange-500">Renseignez le titre pour que les tâches soient générées.</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="mt-6 flex gap-3 pt-4 border-t border-gray-100">
                            <button onClick={handleClose} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                                Annuler
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={!name.trim() || !deadline || (recurrence !== 'none' && !recurrenceTaskTitle.trim())}
                                className="linear flex-1 rounded-xl bg-toola-500 py-2.5 text-sm font-medium text-white transition duration-200 hover:bg-toola-600 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {recurrence !== 'none' && taskCount > 0
                                    ? `Créer + générer ${taskCount} tâche${taskCount > 1 ? 's' : ''}`
                                    : 'Créer le plan'}
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default CreateActionPlanModal;
